import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { Wallet, CheckCircle, ArrowDownToLine, Clock, AlertCircle } from 'lucide-react';

const PartnerPayouts = ({ embedded = false }) => {
  const { getAuthHeaders, API_URL, user, refreshUser } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payoutOpen, setPayoutOpen] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [payoutNotes, setPayoutNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  /* The partner's own tax invoice. Required only when they hold a GSTIN — an unregistered
     partner cannot raise one, and the backend deliberately does not ask them for it. */
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [uploadedInvoice, setUploadedInvoice] = useState(null);
  const [uploadingInvoice, setUploadingInvoice] = useState(false);

  // Payout destination, captured here rather than at signup — this is the moment the partner
  // is motivated to give it. UPI first: one field instead of three, which matters most to the
  // flat-fee tier withdrawing small amounts.
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [method, setMethod] = useState('upi');
  const [upiId, setUpiId] = useState('');
  const [bankAccount, setBankAccount] = useState('');
  const [ifscCode, setIfscCode] = useState('');
  const [accountName, setAccountName] = useState('');
  const [panNo, setPanNo] = useState('');
  const [savingDetails, setSavingDetails] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    setUpiId(user?.upi_id || '');
    setBankAccount(user?.bank_account || '');
    setIfscCode(user?.ifsc_code || '');
    setAccountName(user?.company_name_bank || '');
    setPanNo(user?.pan_no || '');
    if (user?.bank_account && !user?.upi_id) setMethod('bank');
  }, [user]);

  const fetchData = async () => {
    try {
      const [walletRes, requestsRes] = await Promise.all([
        axios.get(`${API_URL}/api/partner/wallet`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/partner/payout-requests`, { headers: getAuthHeaders() }),
      ]);
      setWallet(walletRes.data);
      setRequests(requestsRes.data);
    } catch (error) {
      console.error('Failed to fetch payouts:', error);
      toast.error('Failed to load payouts');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);

  const formatDate = (str) =>
    str ? new Date(str).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

  // Preview of what lands in the bank. Same formula as compute_payout_tax on the backend:
  // net = gross - TDS + GST, GST only for a GST-registered partner.
  const payoutPreview = (() => {
    const gross = parseFloat(payoutAmount);
    if (isNaN(gross) || gross <= 0) return null;
    const tds = (gross * (wallet?.tds_percent ?? 0)) / 100;
    const gst = (gross * (wallet?.gst_percent ?? 0)) / 100;
    return { gross, tds, gst, net: gross - tds + gst };
  })();

  const openPayoutDialog = () => {
    // The backend refuses a request with no destination. Ask here instead of letting the
    // partner fill in an amount and then hit the wall.
    if (!wallet?.payout_details_complete) {
      setDetailsOpen(true);
      return;
    }
    setPayoutAmount(String(Math.floor(wallet?.available_balance || 0)));
    setPayoutNotes('');
    setPayoutOpen(true);
  };

  const saveDetails = async () => {
    const params =
      method === 'upi'
        ? { upi_id: upiId.trim() }
        : { bank_account: bankAccount.trim(), ifsc_code: ifscCode.trim(), company_name_bank: accountName.trim() };
    params.pan_no = panNo.trim();

    if (method === 'upi' && !params.upi_id) {
      toast.error('Enter your UPI id');
      return;
    }
    if (method === 'bank' && !(params.bank_account && params.ifsc_code && params.company_name_bank)) {
      toast.error('Bank transfers need the account number, IFSC and account holder name');
      return;
    }
    if (!params.pan_no) {
      toast.error('Enter your PAN — TDS is deducted at source and we need it to deduct correctly');
      return;
    }

    setSavingDetails(true);
    try {
      // Query parameters, not a JSON body — see update_partner_profile.
      await axios.put(`${API_URL}/api/partner/profile`, null, { params, headers: getAuthHeaders() });
      await refreshUser();
      const walletRes = await axios.get(`${API_URL}/api/partner/wallet`, { headers: getAuthHeaders() });
      setWallet(walletRes.data);
      setDetailsOpen(false);
      toast.success('Payout details saved');
      if (walletRes.data?.can_request_payout) {
        setPayoutAmount(String(Math.floor(walletRes.data?.available_balance || 0)));
        setPayoutNotes('');
        setPayoutOpen(true);
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to save payout details');
    } finally {
      setSavingDetails(false);
    }
  };

  /* Read off the WALLET, not the session user.
     `user` is the cached session and can be minutes stale — a GSTIN added since sign-in, or
     an admin in a support session that began before it existed, both leave it blank while the
     server already thinks otherwise. The wallet is fetched on load and `gst_percent` is set by
     the same server-side predicate as the gate (`gstin` on file), so the button and the
     backend can never disagree. Getting this wrong hid the whole upload step. */
  const needsInvoice = (wallet?.gst_percent ?? 0) > 0;

  /* Upload happens BEFORE the request, not with it, so the invoice is a record in its own
     right rather than an attachment on a payout. That is what lets an admin list and track
     invoices, and what lets one be rejected without unpicking the withdrawal. */
  const uploadInvoice = async () => {
    if (!invoiceFile) { toast.error('Choose your invoice file first.'); return; }
    if (!invoiceNumber.trim()) { toast.error('Enter the invoice number as printed on it.'); return; }
    if (!invoiceDate) { toast.error('Enter the invoice date.'); return; }
    const amount = parseFloat(payoutAmount);
    setUploadingInvoice(true);
    try {
      const form = new FormData();
      form.append('invoice_number', invoiceNumber.trim());
      form.append('invoice_date', invoiceDate);
      form.append('amount', isNaN(amount) ? 0 : amount);
      form.append('file', invoiceFile);
      const res = await axios.post(`${API_URL}/api/partner/invoices`, form, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      setUploadedInvoice(res.data);
      toast.success('Invoice uploaded.');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not upload that invoice');
    } finally {
      setUploadingInvoice(false);
    }
  };

  const submitPayout = async () => {
    const amount = parseFloat(payoutAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    if (needsInvoice && !uploadedInvoice) {
      toast.error('Upload your tax invoice for this amount first.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/partner/payout-requests`,
        { amount, notes: payoutNotes, invoice_id: uploadedInvoice?.id || null },
        { headers: getAuthHeaders() }
      );
      toast.success('Payout request submitted!');
      setPayoutOpen(false);
      setUploadedInvoice(null);
      setInvoiceNumber('');
      setInvoiceDate('');
      setInvoiceFile(null);
      fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to submit payout request');
    } finally {
      setSubmitting(false);
    }
  };

  const statusBadge = (status) => {
    const styles = {
      requested: 'bg-amber-100 text-amber-700 border-amber-200',
      approved: 'bg-blue-100 text-blue-700 border-blue-200',
      paid: 'bg-green-100 text-green-700 border-green-200',
      rejected: 'bg-red-100 text-red-700 border-red-200',
    };
    return (
      <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize border ${styles[status] || styles.requested}`}>
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#ef785b]"></div>
        </div>
      </DashboardLayout>
    );
  }

  const body = (
      <div className="space-y-6" data-testid="partner-payouts">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          {embedded ? <div /> : (
            <div>
              <h1 className="text-2xl font-bold text-[#1A1A1A]">
                Payouts
              </h1>
              <p className="text-sm text-[#64748B] mt-1">
                Withdraw your earned commission
              </p>
            </div>
          )}
          <Button
            onClick={openPayoutDialog}
            disabled={!wallet?.can_request_payout}
            className="bg-[#ef785b] hover:bg-[#D44017] text-white disabled:opacity-50"
            data-testid="request-payout-btn"
          >
            <ArrowDownToLine className="w-4 h-4 mr-2" /> Request Payout
          </Button>
        </div>

        {/* Balance cards */}
        <div className="grid md:grid-cols-2 gap-4">
          <Card className="bg-white border-[#E2E2DF]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#ef785b]/10 flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-[#ef785b]" />
                </div>
                <div>
                  <p className="text-xs text-[#64748B] uppercase tracking-wider">Available to Withdraw</p>
                  <p className="text-xl font-bold text-[#1A1A1A]" data-testid="available-balance">
                    {formatCurrency(wallet?.available_balance)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-[#E2E2DF]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-[#64748B] uppercase tracking-wider">Paid Out to Date</p>
                  <p className="text-xl font-bold text-green-600" data-testid="paid-total">
                    {formatCurrency(wallet?.paid_total)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Prompt #2: the partner has money and nowhere to send it. Loudest once the balance
            clears the threshold, because that is when it actually blocks them. */}
        {!wallet?.payout_details_complete && wallet?.available_balance > 0 && (
          <div
            className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3"
            data-testid="payout-details-banner"
          >
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <div className="flex-1">
              <p className="text-xs text-[#64748B]">
                <span className="font-semibold text-[#1A1A1A]">
                  {!wallet?.payout_destination_complete
                    ? 'No payout details on file.'
                    : 'PAN missing.'}
                </span>{' '}
                {!wallet?.payout_destination_complete
                  ? wallet?.available_balance >= (wallet?.min_payout || 0)
                    ? 'You have enough to withdraw — add a bank account or UPI id to request a payout.'
                    : 'Add a bank account or UPI id so we can pay you when your balance is ready.'
                  : 'TDS is deducted at source, so we need your PAN before we can pay you.'}
              </p>
            </div>
            <Button
              onClick={() => setDetailsOpen(true)}
              className="h-8 shrink-0 bg-[#ef785b] hover:bg-[#D44017] text-white text-xs"
              data-testid="add-payout-details-btn"
            >
              Add details
            </Button>
          </div>
        )}

        {wallet?.held_balance > 0 && (
          <div className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
            <Clock className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
            <p className="text-xs text-[#64748B]">
              <span className="font-semibold text-[#1A1A1A]">{formatCurrency(wallet.held_balance)} held.</span>{' '}
              Earned before your account became active. It is released for withdrawal as soon as an
              admin approves you, and it is not counted in the balance above.
            </p>
          </div>
        )}

        {!wallet?.can_request_payout && (
          <p className="text-xs text-[#64748B] flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" />
            {wallet?.open_payout_request
              ? 'You have a payout request in progress.'
              : `Minimum ${formatCurrency(wallet?.min_payout)} available balance required to request a payout.`}
          </p>
        )}

        {/* Payout Requests */}
        <Card className="bg-white border-[#E2E2DF] overflow-hidden">
          <div className="p-4 border-b border-[#E2E2DF]">
            <h2 className="text-sm font-semibold text-[#1A1A1A]">Payout Requests</h2>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#FAFAF8] hover:bg-[#FAFAF8]">
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Requested</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Amount</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Status</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Processed</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Reference</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {requests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-[#64748B]">
                      No payout requests yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  requests.map((r) => (
                    <TableRow key={r.id} className="hover:bg-[#FAFAF8]/50">
                      <TableCell className="text-sm text-[#64748B]">{formatDate(r.requested_at)}</TableCell>
                      <TableCell className="font-medium text-[#1A1A1A]">{formatCurrency(r.amount)}</TableCell>
                      <TableCell>{statusBadge(r.status)}</TableCell>
                      <TableCell className="text-sm text-[#64748B]">{formatDate(r.processed_at)}</TableCell>
                      <TableCell className="text-sm text-[#64748B]">{r.reference_number || '—'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </Card>

        {/* Payout details — prompt #3, shown in place of the payout dialog when there is
            nowhere to send the money. */}
        <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
          <DialogContent className="max-w-md" data-testid="payout-details-dialog">
            <DialogHeader>
              <DialogTitle>Where should we send your payout?</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex gap-2">
                {[
                  { key: 'upi', label: 'UPI' },
                  { key: 'bank', label: 'Bank account' },
                ].map((m) => (
                  <button
                    key={m.key}
                    type="button"
                    onClick={() => setMethod(m.key)}
                    className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                      method === m.key
                        ? 'border-[#ef785b] bg-[#ef785b]/10 text-[#ef785b]'
                        : 'border-[#E2E2DF] text-[#64748B] hover:bg-[#FAFAF8]'
                    }`}
                    data-testid={`payout-method-${m.key}`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>

              {method === 'upi' ? (
                <div>
                  <label className="text-sm font-medium text-[#1A1A1A] block mb-1.5">UPI ID</label>
                  <Input
                    value={upiId}
                    onChange={(e) => setUpiId(e.target.value)}
                    placeholder="yourname@okhdfcbank"
                    className="h-10 border-[#E2E2DF]"
                    data-testid="payout-upi-id"
                  />
                  <p className="mt-1.5 text-xs text-[#64748B]">Fastest option — one field, and payouts land the same day.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-[#1A1A1A] block mb-1.5">Account number</label>
                    <Input
                      value={bankAccount}
                      onChange={(e) => setBankAccount(e.target.value)}
                      placeholder="123456789012"
                      className="h-10 border-[#E2E2DF]"
                      data-testid="payout-bank-account"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#1A1A1A] block mb-1.5">IFSC code</label>
                    <Input
                      value={ifscCode}
                      onChange={(e) => setIfscCode(e.target.value.toUpperCase())}
                      placeholder="HDFC0001234"
                      className="h-10 border-[#E2E2DF]"
                      data-testid="payout-ifsc"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#1A1A1A] block mb-1.5">Account holder name</label>
                    <Input
                      value={accountName}
                      onChange={(e) => setAccountName(e.target.value)}
                      placeholder="As printed on your bank records"
                      className="h-10 border-[#E2E2DF]"
                      data-testid="payout-account-name"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-[#1A1A1A] block mb-1.5">PAN</label>
                <Input
                  value={panNo}
                  onChange={(e) => setPanNo(e.target.value.toUpperCase())}
                  placeholder="ABCDE1234F"
                  className="h-10 border-[#E2E2DF]"
                  data-testid="payout-pan"
                />
                <p className="mt-1.5 text-xs text-[#64748B]">
                  TDS is deducted at source. Without your PAN we cannot deduct it correctly.
                </p>
              </div>

              <p className="text-xs text-[#64748B]">
                You can change these any time in your Profile. We'll email you whenever they change.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDetailsOpen(false)} className="border-[#E2E2DF]">
                Cancel
              </Button>
              <Button
                onClick={saveDetails}
                disabled={savingDetails}
                className="bg-[#ef785b] hover:bg-[#D44017] text-white"
                data-testid="save-payout-details"
              >
                {savingDetails ? 'Saving...' : 'Save and continue'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Payout Dialog */}
        <Dialog open={payoutOpen} onOpenChange={setPayoutOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Request Payout</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-[#64748B]">
                Available balance: <span className="font-semibold text-[#1A1A1A]">{formatCurrency(wallet?.available_balance)}</span>
              </p>
              <div>
                <label className="text-sm font-medium text-[#1A1A1A] block mb-1.5">Amount (₹)</label>
                <Input
                  type="number"
                  value={payoutAmount}
                  onChange={(e) => setPayoutAmount(e.target.value)}
                  className="h-10 border-[#E2E2DF]"
                  data-testid="payout-amount"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-[#1A1A1A] block mb-1.5">Notes (optional)</label>
                <Input
                  value={payoutNotes}
                  onChange={(e) => setPayoutNotes(e.target.value)}
                  placeholder="Any details for the payout..."
                  className="h-10 border-[#E2E2DF]"
                />
              </div>
              {/* What actually lands in the bank. Mirrors compute_payout_tax on the backend. */}
              {payoutPreview && (
                <div className="rounded-lg border border-[#E2E2DF] bg-[#FAFAF8] p-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-[#64748B]">Commission</span>
                    <span className="font-mono tabular-nums text-[#1A1A1A]">{formatCurrency(payoutPreview.gross)}</span>
                  </div>
                  <div className="mt-1.5 flex items-center justify-between text-sm">
                    <span className="text-[#64748B]">TDS ({wallet?.tds_percent ?? 2}%)</span>
                    <span className="font-mono tabular-nums text-[#1A1A1A]">- {formatCurrency(payoutPreview.tds)}</span>
                  </div>
                  {payoutPreview.gst > 0 && (
                    <div className="mt-1.5 flex items-center justify-between text-sm">
                      <span className="text-[#64748B]">GST ({wallet?.gst_percent ?? 18}%)</span>
                      <span className="font-mono tabular-nums text-[#1A1A1A]">+ {formatCurrency(payoutPreview.gst)}</span>
                    </div>
                  )}
                  <div className="mt-2 flex items-center justify-between border-t border-[#E2E2DF] pt-2 text-sm">
                    <span className="font-medium text-[#1A1A1A]">You receive</span>
                    <span className="font-mono font-semibold tabular-nums text-[#1A1A1A]">{formatCurrency(payoutPreview.net)}</span>
                  </div>
                  {payoutPreview.gst === 0 && (
                    <p className="mt-2 text-xs text-[#64748B]">
                      No GST added. Add a GSTIN in your Profile if you are GST registered.
                    </p>
                  )}
                </div>
              )}
              {/* The invoice, and only for a registered partner.
                  It sits AFTER the tax breakdown on purpose: the partner needs to know the
                  figure before they can raise a document for it. We cannot generate this —
                  a tax invoice carries the supplier's own GSTIN and serial number, and the
                  partner is the supplier here. */}
              {needsInvoice && (
                <div className="rounded-lg border border-[#E2E2DF] p-3">
                  <p className="text-sm font-medium text-[#1A1A1A]">Your tax invoice</p>
                  {uploadedInvoice ? (
                    <p className="mt-1.5 text-sm text-[#16a34a]" data-testid="invoice-attached">
                      {uploadedInvoice.invoice_number} attached.{' '}
                      <button
                        type="button"
                        className="text-[#64748B] underline"
                        onClick={() => setUploadedInvoice(null)}
                      >
                        Use a different one
                      </button>
                    </p>
                  ) : (
                    <>
                      <p className="mt-1 text-xs text-[#64748B]">
                        You are GST registered, so the invoice has to come from your own books —
                        your GSTIN, your serial number. Raise it for{' '}
                        <span className="font-medium text-[#1A1A1A]">
                          {formatCurrency((payoutPreview?.gross || 0) + (payoutPreview?.gst || 0))}
                        </span>{' '}
                        (commission plus GST) and upload it here. TDS is deducted when we pay,
                        so it does not appear on your invoice.
                      </p>
                      <div className="mt-3 grid grid-cols-2 gap-2">
                        <Input
                          value={invoiceNumber}
                          onChange={(e) => setInvoiceNumber(e.target.value)}
                          placeholder="Invoice number"
                          className="h-9 border-[#E2E2DF]"
                          data-testid="invoice-number"
                        />
                        <Input
                          type="date"
                          value={invoiceDate}
                          onChange={(e) => setInvoiceDate(e.target.value)}
                          className="h-9 border-[#E2E2DF]"
                          data-testid="invoice-date"
                        />
                      </div>
                      <input
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                        onChange={(e) => setInvoiceFile(e.target.files?.[0] || null)}
                        className="mt-2 w-full text-xs text-[#64748B]"
                        data-testid="invoice-file"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={uploadInvoice}
                        disabled={uploadingInvoice}
                        className="mt-2 h-9 w-full border-[#E2E2DF] text-sm"
                        data-testid="upload-invoice"
                      >
                        {uploadingInvoice ? 'Uploading…' : 'Upload invoice'}
                      </Button>
                    </>
                  )}
                </div>
              )}

              <p className="text-xs text-[#64748B]">
                Payouts are sent to the bank/UPI details in your Profile. Make sure they're up to date.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPayoutOpen(false)} className="border-[#E2E2DF]">
                Cancel
              </Button>
              <Button
                onClick={submitPayout}
                disabled={submitting}
                className="bg-[#ef785b] hover:bg-[#D44017] text-white"
              >
                {submitting ? 'Submitting...' : 'Submit Request'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
  );

  return embedded ? body : <DashboardLayout>{body}</DashboardLayout>;
};

export default PartnerPayouts;